/**
 * BoardPane — the workbench's diagram primitive.
 *
 * A thin binding of the shared `ArchitectureView` (board + optional sidebar) to
 * the `WorkspaceStore`: it feeds the store's working files into the incremental
 * model pipeline and mirrors selection through the store. A board click writes
 * the selection to the store (so the sidebar follows) AND emits a `reveal` so
 * the editor jumps to the clicked node's declaration. Like every primitive it
 * depends only on the store, so it can be omitted or swapped.
 *
 * The board recompile is debounced: the store hands a fresh `Vfs` per keystroke
 * for instant editor + LSP feedback, but the (heavier) board re-solve coalesces
 * so rapid typing doesn't thrash layout. Incrementality is preserved —
 * `ArchitectureView`'s `useModel`/`IncrementalViewer` still diffs file-by-file.
 *
 * Both directions are live: board → editor is the click-to-reveal above; the
 * reverse, edit/selection → board, is the camera operator ("smart view"), which
 * ArchitectureView wires from the shared model + selection when `operator` is
 * on. It's a board-owned, persisted, off-by-default toggle, so the default stays
 * the no-move baseline and power users opt in.
 */
import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  ArchitectureView,
  type ArchitectureViewHandle,
} from "../../architecture-view/ArchitectureView.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { revealForSelect } from "./selectReveal.js";
import { bareTypeName } from "../../typeKey.js";
import {
  useWorkspaceFiles,
  useWorkspaceSelection,
  useActiveFile,
  useBlastRadius,
  useSelectedTypeName,
} from "../WorkspaceStore.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { createSpanLookup } from "@archlang/engine";
import { useBoardEditing } from "../useBoardEditing.js";
import { BoardEditPopover } from "./BoardEditPopover.js";
import { useActivePackage } from "../useActivePackage.js";
import { createSpaceViewportMemory } from "../../board/spaceViewportMemory.js";
import { debounce } from "../../viewerStorage.js";
import {
  declaredNamesInRange,
  enclosingDeclarations,
  findDeclaration,
  referencedNamesInSelection,
} from "../sourceMap.js";
import { caretChainIds, isCopyWordSelection } from "../caretMap.js";
import { scopedFilesForId } from "../boardSourceMap.js";
import type { MenuItem } from "../nav/ContextMenu.js";
import { Button, useContextMenu } from "@archlang/ui";
import { useContextMenuContributions } from "../plugins/PluginRegistryContext.js";
import { useWorkbenchHost, isActionable } from "../plugins/hostContext.js";
import type { ContextMenuTarget } from "../plugins/types.js";
import type { OverflowItem } from "../../components/OverflowToolbar.js";
import {
  flattenModulesWithParent,
  moduleKey,
} from "@archlang/scene/arch-model";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import type { Viewport } from "@archlang/scene/board";
import type { ItemDiagnostic } from "../../board/index.js";
import type { HoverStore } from "../../board/hoverStore.js";
import type { Vfs } from "@archlang/viewer-core";
import { useModelIndexes } from "../../hooks/useModelIndexes.js";
import { buildDiagnosticsByModule } from "../../derivations.js";
import { useProcessPlaybackContextOptional } from "../ProcessPlaybackContext.js";
import { usePlacementsContextOptional } from "../PlacementsContext.js";
import { useLayoutContextOptional } from "../LayoutContext.js";
import { useBoardHandleOptional } from "../BoardHandleContext.js";
import { useAspectOverlay } from "../../hooks/useAspectOverlay.js";
import { LifecycleProvider } from "../../board/lifecycle/LifecycleProvider.js";
import { BoardFocusProvider } from "../../board/focus/BoardFocusProvider.js";
import { boardFocusKeys } from "../../board/focus/viewFocusKeys.js";
import { useLifecycleDelta } from "../../board/lifecycle/useLifecycleState.js";
import { useViewScrubSeed } from "../../board/lifecycle/useViewScrubSeed.js";
import { lifecycleWindowsOf, type LifecycleWindow } from "@archlang/engine";
import {
  NodeColoringProvider,
  buildAspectColoring,
  buildTypeColoring,
  buildLifecycleDeltaColoring,
  buildViewColoring,
  type NodeColoring,
} from "../../board/coloring/index.js";
import { useEffectiveViewName } from "../../views/useEffectiveViewName.js";
import { useActiveView } from "../ActiveViewContext.js";

export interface BoardPaneProps {
  /** Pin the package root to render (a VFS path). Omit to follow the active
   *  file's package (VSCode-style auto-switch). */
  readonly rootPath?: string;
  /** localStorage namespace for the per-space viewport memory. Pick one per
   *  distinct workspace to avoid space-key collisions. Default "workbench". */
  readonly viewportNamespace?: string;
  /** Render the details sidebar to the right of the board. */
  readonly showSidebar?: boolean;
  /** Handle wheel for pan/zoom (default true). */
  readonly wheel?: boolean;
  /** Make the camera operator ("smart view") available — the board gains a
   *  follow-edits toggle that gently follows edits/selection. Default true (the
   *  workbench is the editing surface); the on/off state is persisted + off by
   *  default, so it stays opt-in. */
  readonly operator?: boolean;
  /** Force smart-view permanently on with no toggle (e.g. the marketing demo) —
   *  the camera always follows edits. Ignores the persisted preference. */
  readonly operatorForced?: boolean;
  /** Mirror of the inner view handle, for chrome that needs the board's
   *  imperative surface (export menus run focal captures). The pane still
   *  feeds everything stateful through the store — this is only for
   *  inherently board-bound actions a store can't carry. */
  readonly handleRef?: MutableRefObject<ArchitectureViewHandle | null>;
  readonly className?: string;
  /** Phone layout — flips the board to its mobile chrome. The host (the viewer's
   *  `useMobileLayout`) decides; omit for desktop. */
  readonly mobile?: boolean;
  /** Phone settings menu (render prop; `close` dismisses on a pick). */
  readonly mobileMenu?: (close: () => void) => ReactNode;
  /** Phone header search button → host's ⌘F modal. */
  readonly onMobileSearch?: () => void;
  /** Host content before the breadcrumb in the board chrome (nav-panel toggle). */
  readonly chromeLeading?: ReactNode;
  /** Action buttons for the board top bar's trailing slot (view controls). */
  readonly chromeTrailingItems?: readonly OverflowItem[];
  // --- Viewer-parity overrides (host injects; omit → store-driven defaults) ---
  // These let the read-only viewer mount BoardPane as a drop-in for its bespoke
  // InfiniteBoard at the swap (board-conversion step 6) without losing its
  // bespoke behaviour. The workbench / embeds omit them and keep the defaults.
  /** Board↔nav hover channel. Default: the store's own `hover`. The viewer
   *  injects its shared instance so its WorkspacePanel stays linked. */
  readonly hoverStore?: HoverStore;
  /** Double-click "bring into view". Default: `store.requestNavReveal`. The
   *  viewer injects its nav-reveal handler. */
  readonly onItemOpen?: (id: string) => void;
  /** Cmd/Ctrl-click an item → open it in a new focused board tab. Opt-in: the
   *  viewer wires this to `store.openTab({ kind: "board", rootId })`. */
  readonly onItemOpenRooted?: (id: string, isSpace?: boolean) => void;
  /** Board `resetKey` override (re-frame only on a genuine workspace swap).
   *  Default: the active space key. The viewer keys on its workspace label. */
  readonly viewportKey?: string | null;
  /** Initial viewport to restore. Default: the per-space remembered viewport.
   *  The viewer injects its session-restored viewport. */
  readonly initialViewport?: Viewport;
  /** Viewport-change handler. Default: per-space memory + `store.reportViewport`.
   *  When provided, the host OWNS persistence (and must call reportViewport). */
  readonly onViewportChange?: (vp: Viewport) => void;
  /** Dev layout-debug overlay. Default off. */
  readonly debugLayout?: boolean;
  /** DEV-only chips docked above the minimap (layout debug controls). */
  readonly minimapExtras?: ReactNode;
  /** Right-click context menu on board items ("Go to definition"). Default on;
   *  set false for surfaces where opening a file tab makes no sense. */
  readonly contextMenu?: boolean;
  /** v0.9.0 §1.1: node ids to FOCUS (plane switcher). When non-empty it takes
   *  precedence over the aspect-legend / type-instance member outline, so
   *  selecting a plane dims the board to that plane's presence subset. */
  readonly memberHighlightOverride?: readonly string[];
  /** Enable board structural editing (W2, WYSIWYG plan §7): context-menu
   *  add/rename/delete/draw-edge + F2/Delete/⌘Z. Hosts pass the same
   *  condition that gates the editor; absent = today's read-only board. */
  readonly editable?: boolean;
}

export function BoardPane(props: BoardPaneProps): ReactElement {
  const store = useWorkspace();
  const files = useWorkspaceFiles(store);
  const activeFile = useActiveFile(store);
  const selection = useWorkspaceSelection(store);
  const blastRadius = useBlastRadius(store);
  const modelState = useWorkspaceModel(); // shared compile (board + nav)
  // Diagnostic badges: the shared model's diagnostics mapped to their owning
  // module ids — same derivation the viewer feeds its direct InfiniteBoard.
  const indexes = useModelIndexes(modelState.model);
  const diagnosticsByItem = useMemo<
    ReadonlyMap<string, readonly ItemDiagnostic[]>
  >(() => {
    // buildDiagnosticsByModule yields the full core Diagnostics per owning
    // module; flatten each to the board's ItemDiagnostic, lifting its source
    // span (1-based line/column + file) so the issue popover can jump to the
    // EXACT spot — not just the module declaration.
    const byModule = buildDiagnosticsByModule(modelState.diagnostics, indexes);
    const out = new Map<string, readonly ItemDiagnostic[]>();
    for (const [id, diags] of byModule) {
      out.set(
        id,
        diags.map((d) => ({
          severity: d.severity,
          message: d.message,
          path: d.source?.file,
          line: d.source?.start.line,
          column: d.source?.start.column,
        })),
      );
    }
    return out;
  }, [modelState.diagnostics, indexes]);
  // Process step decorations — present only when a ProcessPlaybackProvider is
  // mounted above (the viewer / editable viewer). Compositions without playback
  // (marketing demo, Studio preview) get null → no process overlay.
  const playback = useProcessPlaybackContextOptional();
  // Hand-placement service — shared persisted overrides + undo/redo when a
  // PlacementsProvider is mounted (viewer / editable viewer); null in provider-
  // less compositions (demo/Studio), where the board keeps ephemeral local
  // placements.
  const placements = usePlacementsContextOptional();
  // Shared board layout service — one solve feeds the board AND App's details
  // rail when a LayoutProvider is mounted; null in provider-less compositions
  // (demo/Studio), where ArchitectureView solves its own layout.
  const layout = useLayoutContextOptional();
  // Aspect overlay controller — drives the member-highlight outline + the
  // "colour by" node coloring from the store's labels.slice + the shared model.
  const aspect = useAspectOverlay(store, modelState.model);
  // Unified node coloring (board fills + minimap + legend read it via context).
  // Priority: an ACTIVE VIEW with color styles (S15 — `style { color: <cast> }`)
  // takes over; else aspect "colour by"; else the by-type default. Null with no
  // model — same NodeColoring the viewer builds for its direct InfiniteBoard.
  const model = modelState.model;
  const { aspectColorKey } = aspect;
  // The legend titles with the SAME name the plan was computed under — the
  // tab-aware EFFECTIVE view name (a view tab's own view wins over the HUD
  // picker; useViewSurface/LayoutProvider follow the same source). The HUD's
  // raw `activeViewName` could disagree on a view tab and mistitle the legend.
  const effectiveViewName = useEffectiveViewName();
  // Click-reveal (VIEWS §7): reveal actions target the ACTIVE view's
  // session set (drafts under the reserved ::draft key).
  const { revealsByView, revealKey, clearReveals, draftView } = useActiveView();
  const revealViewKey = draftView ? "::draft" : effectiveViewName;
  const onRevealBoundary = useCallback(
    (peerKey: string) => {
      if (revealViewKey) revealKey(revealViewKey, peerKey);
    },
    [revealKey, revealViewKey],
  );
  const viewPlan = layout?.viewPlan ?? null;
  // Count only reveals actually IN the plan's selection — a renamed/deleted
  // module leaves a stale key behind (harmless to the plan, but the pill
  // must not read "N revealed" for nodes that aren't on the board).
  const revealCount = revealViewKey
    ? (revealsByView[revealViewKey] ?? []).filter((k) => viewPlan?.shown.has(k)).length
    : 0;
  const viewNodeIds = layout?.viewNodeIds ?? null;
  const viewColoring = useMemo<NodeColoring | null>(
    () =>
      viewPlan
        ? buildViewColoring(viewPlan, viewNodeIds, effectiveViewName ?? undefined)
        : null,
    [viewPlan, viewNodeIds, effectiveViewName],
  );
  const baseColoring = useMemo<NodeColoring | null>(
    () =>
      viewColoring ??
      (model
        ? aspectColorKey
          ? buildAspectColoring(model.modules, aspectColorKey)
          : // Default: colour by type — re-applies each node's own colour (no
            // visual change) but carries a legend the board/minimap agree with.
            buildTypeColoring(model.modules)
        : null),
    [model, aspectColorKey, viewColoring],
  );
  // Lifecycle windows — straight off the MODEL, which is where every consumer
  // reads them: they key on `moduleKey` (stable id, else `Space::Path`), the
  // way the board keys a node. Scrub-independent — no scrub point is read here,
  // which is what keeps a scrub tick a repaint rather than a re-solve.
  // Membership is unaffected: a window for a node the view hides simply never
  // renders, which is what keeps this a paint channel.
  const lifecycleWindows = useMemo<ReadonlyMap<string, LifecycleWindow> | undefined>(
    () => (modelState.resolvedModel ? lifecycleWindowsOf(modelState.resolvedModel) : undefined),
    [modelState.resolvedModel],
  );
  // A view's own `at` clause moves the scrubber when that view becomes active.
  useViewScrubSeed(effectiveViewName, viewPlan?.at);
  // Two-point roadmap delta. With a second scrub handle down, the board colours
  // by what CHANGES between the two points instead of by team or type — the same
  // vocabulary a git diff uses, because to a reader it is the same question
  // asked across time. Falls back to the ordinary colouring the moment the
  // second handle is lifted.
  const lifecycleDelta = useLifecycleDelta(lifecycleWindows, modelState.resolvedModel?.timeline);
  const nodeColoring = useMemo<NodeColoring | null>(
    () => (lifecycleDelta.size > 0 ? buildLifecycleDeltaColoring(lifecycleDelta) : baseColoring),
    [lifecycleDelta, baseColoring],
  );
  // Hover/pin a legend row → outline that row's members. Driven by the ACTIVE
  // coloring's own legend memberIds, so it works for type (default), aspect, and
  // the N/A row alike — not just aspect.
  const legendValue = aspect.legendHoverValue ?? aspect.legendPinnedValue;
  const coloringHighlightIds = useMemo<readonly string[] | undefined>(() => {
    if (!nodeColoring || !legendValue) return undefined;
    const entry = nodeColoring.legend.find((e) => e.id === legendValue);
    return entry && entry.memberIds.length > 0 ? entry.memberIds : undefined;
  }, [nodeColoring, legendValue]);
  // View `focus` — a STANDING property of the view, so it gets its OWN channel
  // (occlusion: the ground is veiled, the figure is left alone) instead of the
  // member-outline socket it used to share. In that socket it was the last term
  // of a `??` chain, which meant a legend hover ERASED the view's emphasis
  // rather than layering over it, and the yellow ring meant both "the author
  // says these matter" and "the neighbourhood of what I clicked".
  //
  // The plan keys on origin-qualified names; the board keys on ids, with one
  // origin fanning out to every `::m::` copy on a `group by` board — so the
  // mapping happens once, in `boardFocusKeys`, and `BoardFocusProvider` hands
  // the board a set of BOARD keys.
  const viewFocusKeys = useMemo<ReadonlySet<string> | undefined>(
    () => boardFocusKeys(viewPlan?.focus, viewNodeIds),
    [viewPlan, viewNodeIds]
  );
  // "Select all instances of a type" — the store's selectedTypeName slice maps
  // to the member ids the board outlines (when no legend value is active).
  const selectedTypeName = useSelectedTypeName(store);
  const typeInstanceIds = useMemo<readonly string[] | undefined>(() => {
    if (!model || !selectedTypeName) return undefined;
    // The slice holds a selection key (`Space::name`) or a bare name; a
    // module's `typeName` is always the as-written bare name (r56).
    const bare = bareTypeName(selectedTypeName);
    const ids = flattenModulesWithParent(model.modules)
      .filter((m) => m.typeName === bare)
      .map((m) => moduleKey(m));
    return ids.length > 0 ? ids : undefined;
  }, [model, selectedTypeName]);
  const filesRef = useRef<Vfs>(files);
  filesRef.current = files;
  const modelRef = useRef<ArchitectureModel | null>(modelState.model);
  modelRef.current = modelState.model;
  // Span-accurate board→source mapping (W0, WYSIWYG plan §4): resolve a node id
  // through the compiler's own decl spans. The heuristic text scan below stays
  // as the fallback for ids the resolved model can't place (e.g. mid-typing,
  // compile lagging the store).
  const { resolvedModel } = modelState;
  const spanLookup = useMemo(
    () => (resolvedModel ? createSpanLookup(resolvedModel) : null),
    [resolvedModel],
  );
  const spanLookupRef = useRef(spanLookup);
  spanLookupRef.current = spanLookup;
  // Compiled spans carry absolute ("/"-prefixed) VFS paths; the store may key
  // bare (Studio). Translate to the STORE's key so reveals/tabs resolve.
  const storeKeyForFile = (absFile: string): string | null => {
    const fs = filesRef.current;
    if (fs.has(absFile)) return absFile;
    const bare = absFile.startsWith("/") ? absFile.slice(1) : absFile;
    return fs.has(bare) ? bare : null;
  };
  const spanDeclLocationFor = (id: string): { path: string; line: number } | null => {
    const loc = spanLookupRef.current?.moduleFor(id);
    if (!loc || loc.materialization === "stub" || !loc.file) return null;
    const anchor = loc.nameSpan ?? loc.span;
    if (!anchor) return null;
    const path = storeKeyForFile(loc.file);
    return path ? { path, line: anchor.start.line } : null;
  };

  // Board structural editing (W2): state machine + menu items + keyboard.
  const boardEditing = useBoardEditing(props.editable === true, indexes);
  useEffect(() => {
    if (!boardEditing) return;
    const onKey = (e: KeyboardEvent): void =>
      boardEditing.onKeyDown(e, store.getState().selection.moduleId);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [boardEditing, store]);

  // Per-space viewport memory: which space the open file belongs to keys the
  // remembered pan/zoom. Switching the active package/space restores its last
  // viewport (via the board's resetKey effect) instead of carrying one shared
  // viewport that drifts off-center.
  const { spaceKey } = useActivePackage(files, activeFile, props.rootPath);
  const memoryRef = useRef(
    createSpaceViewportMemory(props.viewportNamespace ?? "workbench"),
  );
  const spaceKeyRef = useRef(spaceKey);
  spaceKeyRef.current = spaceKey;
  // When an external viewportKey is provided (e.g. per-tab isolation in the
  // Studio file-tab companion board), key viewport memory by it instead of
  // spaceKey — each tab starts with no memorised camera (auto-fit) and
  // accumulates its own as the user pans, independent of the overview board.
  const memoryKey: string | null =
    props.viewportKey !== undefined ? (props.viewportKey ?? null) : spaceKey;
  const memoryKeyRef = useRef(memoryKey);
  memoryKeyRef.current = memoryKey;
  // The remembered viewport for the CURRENT space — restored at mount and on a
  // space switch (the board's resetKey effect restores this over the fit when
  // present). Reactive so it tracks the active space. A host can override (the
  // viewer restores its session-persisted viewport instead).
  const memorisedViewport =
    memoryKey !== null ? memoryRef.current.get(memoryKey) : undefined;
  const initialViewport = props.initialViewport ?? memorisedViewport;
  // Persist under the space that was active WHEN the viewport changed (captured
  // at call time), not when the debounce fires — a fast tab switch must not save
  // the old space's viewport under the new space's key.
  const saveViewport = useMemo(
    () =>
      debounce(
        (key: string | null, vp: Viewport) => memoryRef.current.set(key, vp),
        200,
      ),
    [],
  );

  const selectModule = (id: string | null): void => {
    // An armed draw-edge consumes the click as its TARGET pick (W2, §3.3).
    if (boardEditing?.interceptSelect(id)) return;
    // Picking a module clears any edge; but a NULL clear (ArchitectureView
    // fires onSelectChange(null) right after an edge pick) must only clear the
    // module — otherwise it wipes the edge that was just selected. Mirror of the
    // edge handler's asymmetry below.
    store.select(id ? { moduleId: id, edgeId: null } : { moduleId: null });
    // board → editor: reveal the clicked node's declaration (no focus steal),
    // but ONLY when it lives in the file the editor is already showing. A plain
    // SELECT must never switch the active tab — `requestReveal` calls
    // `setActiveFile`, which opens/activates the target file's tab and would
    // yank the user off the overview board (or off the file they're editing)
    // on every click. Cross-file "go to source" is an explicit gesture, not a
    // side effect of selecting a node. Span-accurate via the resolved model;
    // the scoped text scan is the fallback so reveals keep working while a
    // compile lags the store.
    if (id) {
      const loc =
        spanDeclLocationFor(id) ??
        findDeclaration(scopedFilesForId(filesRef.current, modelRef.current, id), id);
      const revealPath = loc ? storeKeyForFile(loc.path) : null;
      const reveal = revealForSelect(
        loc && revealPath !== null ? { path: revealPath, line: loc.line } : null,
        activeFile,
      );
      if (reveal) store.requestReveal(reveal);
    }
  };

  // nav double-click → fly this board to the module (the boardFly event is
  // emitted by the store NavSource's openModule).
  const viewRef = useRef<ArchitectureViewHandle | null>(null);
  useEffect(
    () =>
      store.on((ev) => {
        if (ev.type === "boardFly") viewRef.current?.flyTo(ev.moduleId);
        if (ev.type === "framePair") viewRef.current?.framePair(ev.aId, ev.bId);
      }),
    [store],
  );

  // Board transport (handle + hover channel): the composition may inject them as
  // props, but a bare mount self-sources them from BoardHandleProvider so the
  // board and nav stay linked without prop-drilling. Falls back to the store's
  // own hover for a provider-less embed.
  const boardHandle = useBoardHandleOptional();
  // Mirror the handle out for board-bound chrome (export menus).
  const handleRef = props.handleRef ?? boardHandle?.boardRef;
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = viewRef.current;
    return () => {
      handleRef.current = null;
    };
  });

  // This board's viewport pair is the store's seam: sidebar drill-downs
  // snapshot the camera, `goBack` restores it.
  useEffect(
    () =>
      store.setViewportSeam({
        getViewport: () => viewRef.current?.getViewport() ?? null,
        restoreViewport: (vp) => viewRef.current?.restoreViewport(vp),
      }),
    [store],
  );

  // editor → board: map the editor's text selection (a line range) to the board
  // ids it covers, so the camera operator can frame "what I selected in code".
  // Resolves declared names in the selected range against the live model; the
  // operator stays put when those nodes are already comfortably in view.
  const [operatorSelectionIds, setOperatorSelectionIds] = useState<
    readonly string[]
  >([]);
  // Emitted each time the editor selection clears, so the operator can glide
  // the board back. `graceful` = the caret landed inside one of the framed
  // declarations (typing is probably next → delay the return); caret away →
  // immediate return.
  const [operatorSelectionCleared, setOperatorSelectionCleared] = useState<{
    nonce: number;
    graceful: boolean;
  } | null>(null);
  // Board ids the caret currently sits inside (innermost first) — lets the
  // operator hold a framed node while the caret is parked in it. Recomputed on
  // every caret move (a collapsed selection).
  const [operatorCaretIds, setOperatorCaretIds] = useState<readonly string[]>([]);
  // The names that produced the current framed selection — used on clear to
  // decide whether the caret stayed "within" what was framed.
  const selectionNamesRef = useRef<readonly string[]>([]);
  useEffect(() => {
    return store.on((ev) => {
      if (ev.type !== "editorSelection") return;
      const text = filesRef.current.get(ev.path);
      if (ev.cleared) {
        // Caret move (collapsed selection). Update the caret→id chain FIRST so
        // the operator sees where the cursor landed before it reacts to the
        // selection clearing (hold vs settle turns on this).
        setOperatorCaretIds(
          caretChainIds(text, ev.startLine, modelRef.current, ev.path),
        );
        // Caret inside one of the framed declarations (or their nesting chain)?
        // The cleared event's line IS the caret line (collapsed selection).
        const enclosing = text ? enclosingDeclarations(text, ev.startLine) : [];
        const graceful =
          selectionNamesRef.current.length > 0 &&
          enclosing.some((n) => selectionNamesRef.current.includes(n));
        selectionNamesRef.current = [];
        // Drop the ids (so re-selecting the same span re-frames) and signal the
        // operator to drift back (now or after the grace).
        setOperatorSelectionIds((prev) => (prev.length ? [] : prev));
        setOperatorSelectionCleared((prev) => ({
          nonce: (prev?.nonce ?? 0) + 1,
          graceful,
        }));
        return;
      }
      const model = modelRef.current;
      if (!model || !text) return;
      // Declarations the selection covers, PLUS modules it merely references —
      // a bare step (`Shortener > Keygen`) declares nothing, but selecting one
      // endpoint should frame that module (stub or not), and selecting the
      // whole step frames both ends (the box covers the connection too).
      // Reference tokens that aren't module names drop out at resolution.
      const declared = declaredNamesInRange(text, ev.startLine, ev.endLine);
      const referenced = referencedNamesInSelection(
        text,
        ev.startLine,
        ev.startColumn,
        ev.endLine,
        ev.endColumn,
      );
      // Double-click-a-word guard: a pointer word-select that names ONLY a
      // referenced module (nothing declared in range) is almost always
      // "select to copy", not "frame this" — suppress the framing. A drag-select
      // (not wordLike) or a double-click on a declaration name still frames.
      if (isCopyWordSelection(ev.wordLike ?? false, declared, referenced)) return;
      const names = [...new Set([...declared, ...referenced])];
      if (names.length === 0) return; // nothing nameable selected → leave the view be
      const flat = flattenModulesWithParent(model.modules);
      const ids: string[] = [];
      const seen = new Set<string>();
      for (const name of names) {
        const matches = flat.filter((m) => m.name === name);
        // Prefer a module declared in the selected file; else any name match.
        const pick =
          matches.find((m) => m.sourceFile === ev.path) ?? matches[0];
        if (!pick) continue;
        const key = moduleKey(pick);
        if (!seen.has(key)) {
          seen.add(key);
          ids.push(key);
        }
      }
      if (ids.length > 0) {
        selectionNamesRef.current = names;
        setOperatorSelectionIds(ids);
      }
    });
  }, [store]);

  // Board context menus — one shared composable primitive per surface (node,
  // edge, empty canvas). The node menu's action ("Go to definition") needs the
  // store + resolved model to open a file tab at the declaration. Opt-in: only
  // wired when the host provides a way to edit/open (the viewer), via
  // `onItemOpenRooted`/`onItemContextMenu` being meaningful. The board threads
  // {x,y} coords (not the raw event) up from SpaceRenderer, so these use the
  // hook's `openAt`.
  const nodeMenu = useContextMenu({ sheet: props.mobile });
  const edgeMenu = useContextMenu({ sheet: props.mobile });
  // Plugin-contributed right-click rows, appended after the built-ins. Filtered
  // by target + gated by `when(ctx)` at open time (see `pluginMenuItems`).
  const contextMenuContribs = useContextMenuContributions();
  const hostCtx = useWorkbenchHost();
  const pluginMenuItems = (target: ContextMenuTarget, targetId: string): readonly MenuItem[] => {
    const rows: MenuItem[] = [];
    for (const { contribution } of contextMenuContribs) {
      if (contribution.target !== target) continue;
      if (!isActionable(contribution, hostCtx)) continue;
      for (const it of contribution.items(hostCtx, targetId)) {
        rows.push({ label: it.label, danger: it.danger, separator: it.separator, onClick: it.run });
      }
    }
    // Divide the first plugin row from the built-ins above it.
    if (rows.length > 0 && !rows[0]!.separator) rows[0] = { ...rows[0]!, separator: true };
    return rows;
  };
  // The reusable "open this file in the editor at an exact point, focus the
  // cursor there, and keep the board parked where it is" action. Used by
  // go-to-definition AND the issue popover (and anything else that needs to jump
  // board → code). Carries the CURRENT board viewport onto the freshly-opened
  // file tab so the centre board stays on the clicked node instead of snapping
  // back to the overview (ViewerComposition keys the board per tab and restores
  // each tab's stored viewport).
  const openInEditor = (
    path: string,
    line: number,
    column: number,
    focus: boolean,
  ): void => {
    const vp = viewRef.current?.getViewport() ?? null;
    store.openTab({ kind: "file", path });
    if (vp) store.updateTabState(path, { viewport: vp });
    store.requestReveal({ path, line, column, focus });
  };
  // Resolve a module id → its declaration location. Span-accurate first (the
  // compiler's own decl spans, W0); the scoped heuristic text scan remains the
  // fallback for ids the resolved model can't place.
  const declLocationFor = (id: string): { path: string; line: number } | null => {
    const bySpan = spanDeclLocationFor(id);
    if (bySpan) return bySpan;
    const scoped = scopedFilesForId(filesRef.current, modelRef.current, id);
    let loc = findDeclaration(scoped, id);
    if (!loc && modelRef.current) {
      const m = flattenModulesWithParent(modelRef.current.modules).find(
        (mm) => moduleKey(mm) === id,
      );
      if (m) loc = findDeclaration(scoped, m.name);
    }
    return loc;
  };
  const goToDefinition = (id: string): void => {
    const loc = declLocationFor(id);
    if (loc) openInEditor(loc.path, loc.line, 1, false);
  };
  // Issue popover row click: jump to the diagnostic's EXACT span and focus the
  // cursor there. Diagnostics without a source span fall back to the owning
  // module's declaration.
  const openDiagnostic = (id: string, diag: ItemDiagnostic): void => {
    if (diag.path && diag.line) {
      openInEditor(diag.path, diag.line, diag.column ?? 1, true);
      return;
    }
    const loc = declLocationFor(id);
    if (loc) openInEditor(loc.path, loc.line, 1, true);
  };
  // A module with children is a space → "open in new tab" should root INTO it.
  const isSpaceId = (id: string): boolean => {
    const m = flattenModulesWithParent(modelRef.current?.modules ?? []).find(
      (mm) => moduleKey(mm) === id,
    );
    return Boolean(m?.children?.length);
  };
  const nodeMenuItems = (id: string): readonly MenuItem[] => [
    { label: "Go to definition", onClick: () => goToDefinition(id) },
    ...(props.onItemOpenRooted
      ? [
          {
            label: "Open in new board tab",
            onClick: () => props.onItemOpenRooted!(id, isSpaceId(id)),
          },
        ]
      : []),
    ...(boardEditing ? boardEditing.menuItemsFor(id, nodeMenu.close) : []),
    ...pluginMenuItems("node", id),
  ];

  // Menu for a right-clicked connection / interface port.
  const edgeMenuItems = (edgeId: string): readonly MenuItem[] => [
    { label: "Select connection", onClick: () => store.select({ edgeId, moduleId: null }) },
    { label: "Copy connection id", onClick: () => void navigator.clipboard?.writeText(edgeId) },
    ...pluginMenuItems("edge", edgeId),
  ];

  // Board-level menu: right-click the empty canvas (not on a node). Camera +
  // WYSIWYG root-add actions — the only surface that owns "fit the whole board".
  const bgMenu = useContextMenu({ sheet: props.mobile });
  const backgroundMenuItems = (): readonly MenuItem[] => {
    const items: MenuItem[] = [{ label: "Fit to view", onClick: () => viewRef.current?.reframe() }];
    if (boardEditing)
      items.push({ label: "Add module", separator: true, onClick: () => boardEditing.addRootModule() });
    return items;
  };

  // Chrome: "+ Module" root add (§3.4 — lands in the active file).
  const chromeTrailingItems = useMemo<readonly OverflowItem[] | undefined>(() => {
    if (!boardEditing) return props.chromeTrailingItems;
    const add: OverflowItem = {
      key: "wysiwyg-add-module",
      node: (
        <Button
          variant="ghost"
          size="sm"
          aria-label="Add module"
          title="Add a top-level module"
          onClick={() => boardEditing.addRootModule()}
        >
          + Module
        </Button>
      ),
    };
    return [...(props.chromeTrailingItems ?? []), add];
  }, [boardEditing, props.chromeTrailingItems]);

  return (
    <NodeColoringProvider value={nodeColoring}>
      <LifecycleProvider windows={lifecycleWindows} timeline={modelState.resolvedModel?.timeline}>
      <BoardFocusProvider figure={viewFocusKeys}>
      <ArchitectureView
        ref={viewRef}
        modelState={modelState}
        rootPath={props.rootPath}
        hoverStore={props.hoverStore ?? boardHandle?.hoverStore ?? store.hover}
        onItemOpen={props.onItemOpen ?? ((id) => store.requestNavReveal(id))}
        onItemOpenRooted={props.onItemOpenRooted}
        onItemContextMenu={
          props.contextMenu === false
            ? undefined
            : (id, x, y) => nodeMenu.openAt(x, y, nodeMenuItems(id))
        }
        onBackgroundContextMenu={
          props.contextMenu === false ? undefined : (e) => bgMenu.open(e, backgroundMenuItems())
        }
        onEdgeContextMenu={
          props.contextMenu === false ? undefined : (edgeId, x, y) => edgeMenu.openAt(x, y, edgeMenuItems(edgeId))
        }
        onOpenDiagnostic={openDiagnostic}
        viewportKey={
          props.viewportKey !== undefined ? props.viewportKey : spaceKey
        }
        initialViewport={initialViewport}
        onViewportChange={(vp) => {
          // Host override owns persistence (it must call reportViewport itself);
          // otherwise: per-space memory (debounced) + the store's observed
          // viewport — reportViewport is cheap (no notify unless the focal path
          // changed) and keeps share-view / drill-history fallbacks current.
          if (props.onViewportChange) {
            props.onViewportChange(vp);
            return;
          }
          saveViewport(memoryKeyRef.current, vp);
          store.reportViewport(vp);
        }}
        showSidebar={props.showSidebar}
        wheel={props.wheel}
        operator={props.operator ?? true}
        operatorForced={props.operatorForced}
        operatorSelectionIds={operatorSelectionIds}
        operatorSelectionCleared={operatorSelectionCleared}
        operatorCaretIds={operatorCaretIds}
        className={props.className}
        selectedId={selection.moduleId}
        selectedEdgeId={selection.edgeId}
        // Blast-radius highlight: the store slice the host fills when a module is
        // selected (the connected neighbourhood). Socket parity with the viewer's
        // direct InfiniteBoard `highlightedIds`.
        highlightedIds={blastRadius}
        diagnosticsByItem={diagnosticsByItem}
        // Process step overlay — null-safe (no playback provider → no overlay).
        activeProcessId={playback?.activeProcessId ?? null}
        activeStepIndex={playback?.activeStepIndex ?? null}
        edgeDecoMap={playback?.edgeDecoMap ?? undefined}
        stepActorId={playback?.stepActorId ?? null}
        stepParticipants={playback?.stepParticipants ?? null}
        // Aspect/type member outline (hover/pin a legend value → outline members);
        // falls back to the host's type-instance selection when no aspect value
        // is active (the viewer's "select all instances of a type"). Every term
        // here is a TRANSIENT gesture — the view's standing `focus` is no longer
        // among them: it owns occlusion and layers under all of these, so
        // lighting something in the ground still reads.
        memberHighlightIds={
          props.memberHighlightOverride && props.memberHighlightOverride.length > 0
            ? props.memberHighlightOverride
            : coloringHighlightIds ?? aspect.aspectHighlight?.modules ?? typeInstanceIds
        }
        memberInterfacePaths={aspect.aspectHighlight?.interfaces}
        // Aspect WEB overlay: synthetic aspect nodes + member edges folded into
        // the layout (the pinned web keys). EMPTY_OVERLAY ref-stable when off.
        aspectOverlay={aspect.aspectOverlay}
        // Hand placements: inject the shared service as controlled props when
        // present; undefined → board keeps ephemeral local placements.
        placements={placements?.placements}
        pinnedIds={placements?.pinnedIdSet}
        onPlace={placements?.place}
        onUnpin={placements?.unpin}
        // Shared layout controller: one solve for board + details rail. Undefined
        // → the view solves its own layout (embed fallback).
        layout={layout ?? undefined}
        // Phone chrome pass-throughs (host-fed; absent on desktop/embeds).
        mobile={props.mobile}
        mobileMenu={props.mobileMenu}
        onMobileSearch={props.onMobileSearch}
        chromeLeading={props.chromeLeading}
        chromeTrailingItems={chromeTrailingItems}
        debugLayout={props.debugLayout}
        minimapExtras={props.minimapExtras}
        onSelectChange={selectModule}
        // Selecting an edge clears the module (mutual exclusion); but CLEARING the
        // edge (edgeId === null, which ArchitectureView fires right after a module
        // select) must NOT null the module — that wiped the just-set selection.
        onSelectEdgeChange={(edgeId) =>
          store.select(edgeId ? { edgeId, moduleId: null } : { edgeId: null })
        }
        onRevealBoundary={onRevealBoundary}
      />
      {nodeMenu.node}
      {edgeMenu.node}
      {bgMenu.node}
      {boardEditing && <BoardEditPopover editing={boardEditing} />}
      {revealCount > 0 && (
        <button
          type="button"
          data-hud
          data-hud-region="reveals"
          className={
            // Mobile: bottom-center is thumb/transport territory (StepScrubber)
            // — relocate top-center like the other HUDs vacate the bottom.
            "absolute left-1/2 -translate-x-1/2 z-chrome flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-slate-900/(--tint-solid) px-3 py-1 text-xs text-sky-300 shadow-lg hover:border-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
            (props.mobile ? "top-14" : "bottom-14")
          }
          title="Boundary reveals in this view — click to clear"
          onClick={() => revealViewKey && clearReveals(revealViewKey)}
        >
          {revealCount} revealed
          <span aria-hidden>✕</span>
        </button>
      )}
      {boardEditing?.note && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-scrim px-3 py-1 rounded-full border border-slate-700 bg-slate-900/(--tint-solid) text-xs text-amber-300/90 shadow-lg"
        >
          {boardEditing.note}
        </div>
      )}
      </BoardFocusProvider>
      </LifecycleProvider>
    </NodeColoringProvider>
  );
}
